import React from 'react';
import {
    Button,
    ClickAwayListener,
    Dialog,
    Fade,
    Grid,
    IconButton,
    LinearProgress,
    Popper,
    Typography,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import {Add, Delete, Edit, MoreVert, SubdirectoryArrowRight, Visibility} from "@mui/icons-material";
import {deleteAttribute as utilDeleteAttribute, deleteAttributeSet as utilDeleteAttributeSet, deleteCategory as utilDeleteCategory} from "../../../../../utils/utils";
import EnhancedErrorDialog from "../../../../../components/EnhancedErrorDialog";
import EnhancedConfirmDialog from "../../../../../components/EnhancedConfirmDialog";
import Auth from "../../../../../auth";
import {pageContentGap} from "../../../../../theme";

const StyledMenu = styled(Grid)(({ theme }) => ({
    background: "#FFFFFF",
    border: "1px solid #DCDCDC",
    flexDirection: "column",
    borderRadius: "6px"
}));

const IconContainer = styled('div')(({ theme }) => ({
    width: "100%",
    display: "flex",
    gap: pageContentGap,
    justifyContent: "flex-start",
    fontSize: "20px"
}));


const iconButtonSx = {
    width: "100%",
    justifyContent: "flex-start",
    padding: "12px",
    textTransform: "none",
    borderRadius: "6px",
    border: "none",
    background: "#fff",
    boxShadow: "0px 2px 4px -1px rgba(10, 9, 11, 0.02), 0px 5px 13px -5px rgba(10, 9, 11, 0.05)",
    "&.Mui-disabled": {
        border: "none",
    },
};

interface TaxonomyRow {
    Id?: any;
    Name?: string;
    Level?: number;
    System?: boolean;
    [key: string]: any;
}

interface ErrorState {
    open?: boolean;
    errorHeader?: string;
    errorMessage?: string;
    [key: string]: any;
}

interface SettingsTableMoreOptsProps {
    currentSetting: "categories" | "attributes" | "attributeSets" | string;
    row: TaxonomyRow;
    setConfig?: (updater: (prevState: any) => any) => void;
    setCatalog?: (updater: (prevState: any) => any) => void;
    attributeDialog?: any;
    categoryDialog?: any;
    attributeSetDialog?: any;
    setCategoryDialog?: (dialog: any) => void;
    setAttributeDialog?: (dialog: any) => void;
    setAttributeSetDialog?: (dialog: any) => void;
    disallowDelete?: boolean;
    customActions?: (args: { row: TaxonomyRow; currentSetting: string; open: boolean; setOpen: () => void }) => React.ReactNode;
}

function SettingsTableMoreOpts(props: SettingsTableMoreOptsProps) {
    const hasPermission = Auth.hasPermission("settings/manage");

    // Simple local state - each menu manages itself
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const [error, setError] = React.useState<ErrorState>({});
    const [loading, setLoading] = React.useState(false);
    const [confirmDelete, setConfirmDelete] = React.useState(false);

    const handleMenuToggle = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (open) {
            setAnchorEl(null);
        } else {
            setAnchorEl(e.currentTarget);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const deleteEntity = async () => {
        if (!hasPermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            });
            return;
        }

        setLoading(true);
        let response = undefined;
        if (props.currentSetting === "categories") {
            response = await utilDeleteCategory(props.row.Id);
        } else if (props.currentSetting === "attributes") {
            response = await utilDeleteAttribute(props.row.Name);
        } else if (props.currentSetting === "attributeSets") {
            response = await utilDeleteAttributeSet(props.row.Name);
        }

        if (response !== true && response !== undefined) {
            setError(response);
        } else {
            props.setConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
            await new Promise(r => setTimeout(r, 750));
        }
        setLoading(false);
    }

    return (
        <>
            <IconButton
                data-testid={`poc-taxonomy-row-actions-${props.currentSetting}-${props.row.Name || props.row.Id}`}
                onClick={handleMenuToggle}
                style={{borderRadius: 100}}
                size="large">
                <MoreVert/>
            </IconButton>
            <Dialog open={loading} onClose={(event, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            }}>
                <LinearProgress
                    sx={{
                        width: "100%",
                        maxWidth: "100vw",
                        height: '0.2em',
                        visibility: loading ? undefined : 'hidden'
                    }}/>

                <div style={{display: "flex", flexDirection: "column", gap: pageContentGap, padding: "24px"}}>
                    <Typography variant={"h6"}>Deleting Entity</Typography>
                    <Typography variant={"body1"}>Please wait... </Typography>
                </div>
            </Dialog>
            <Popper open={open} anchorEl={anchorEl} transition placement={"left-start"}
                    style={{zIndex: 1300}}
            >
                {({TransitionProps}) => (
                    <ClickAwayListener onClickAway={handleMenuClose} mouseEvent="onMouseDown">
                        <Fade {...TransitionProps} timeout={350}>
                            <StyledMenu container style={{maxWidth: "300px"}}>
                                {(props.currentSetting === "categories" && hasPermission) && (
                                    <>
                                        <Button sx={iconButtonSx}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    props.setCategoryDialog({
                                                        open: true,
                                                        type: "sibling",
                                                        isEdit: false,
                                                        previousCategory: props.row,
                                                    })
                                                }}
                                        >
                                            <IconContainer>
                                                <Add style={{width: "20px", height: "20px"}}/>
                                                <Typography>Add Sibling Category</Typography>
                                            </IconContainer>
                                        </Button>

                                        <Button sx={iconButtonSx}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    props.setCategoryDialog({
                                                        open: true,
                                                        type: "child",
                                                        isEdit: false,
                                                        previousCategory: props.row,
                                                    })
                                                }}
                                        >
                                            <IconContainer>
                                                <SubdirectoryArrowRight style={{width: "20px", height: "20px"}}/>
                                                <Typography>Add Child Category</Typography>
                                            </IconContainer>
                                        </Button>
                                    </>
                                )}

                                <Button sx={iconButtonSx}
                                        data-testid={`poc-taxonomy-edit-${props.currentSetting}-${props.row.Name || props.row.Id}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (props.currentSetting === "categories") {
                                                props.setCategoryDialog({
                                                    open: true,
                                                    isEdit: true,
                                                    isParent: props.row.Level === 0,
                                                    previousCategory: props.row,
                                                })
                                            } else if (props.currentSetting === "attributes") {
                                                props.setAttributeDialog({
                                                    open: true,
                                                    isEdit: true,
                                                    previousAttribute: props.row,
                                                })
                                            } else if (props.currentSetting === "attributeSets") {
                                                props.setAttributeSetDialog({
                                                    open: true,
                                                    isEdit: true,
                                                    previousAttributeSet: props.row,
                                                })
                                            }
                                            handleMenuClose();
                                        }}
                                >
                                    <IconContainer>
                                        {(props.row.System !== true && hasPermission) ?
                                            <Edit style={{width: "20px", height: "20px"}}/> :
                                            <Visibility style={{width: "20px", height: "20px"}}/>}
                                        <Typography>
                                            {(props.row.System !== true && hasPermission) ? "Edit" : "View"}
                                        </Typography>
                                    </IconContainer>
                                </Button>

                                {(props.row.System !== true && !props.disallowDelete && hasPermission) && (
                                    <Button sx={iconButtonSx} data-testid={`poc-taxonomy-delete-${props.currentSetting}-${props.row.Name || props.row.Id}`} onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setConfirmDelete(true);
                                    }}>
                                        <IconContainer>
                                            <Delete style={{width: "20px", height: "20px"}}/>
                                            <Typography>Delete</Typography>
                                        </IconContainer>
                                    </Button>
                                )}

                                {(props.customActions && props.row.System !== true) && props.customActions({
                                    row: props.row, currentSetting: props.currentSetting, open, setOpen: handleMenuClose
                                })}
                            </StyledMenu>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            <EnhancedConfirmDialog open={confirmDelete} setOpen={setConfirmDelete}
                                   title={"Are you sure you want to delete this entity?"}
                                   message={"This action cannot be undone!"}
                                   type={"delete"}
                                   action={(action) => {
                                       if (action) deleteEntity()
                                   }}
            />
            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
        </>
    );
}

export default SettingsTableMoreOpts;
